import { useEffect } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `account.id` and `company.id` (watched).
 * Writes:   the preloads' `primaryCustomerRate` state, through the loader
 *           pair `useOrderFormV3Preloads` exposes — the account's primary
 *           customer rate seeds FreightRates' default customer rate on new
 *           orders.
 * Guards:   NEW orders only (`!isEditingOrder`) with both an account and a
 *           company selected; any other combination clears the preload. The
 *           loader's request token discards stale resolutions, mirroring the
 *           legacy effect's `isStale` cleanup.
 * Legacy:   the legacy form's primary customer rate fetch.
 * Test:     __tests__/useOrderFormV3Preloads.test.ts (fetch shape) +
 *           __tests__/OrderFormV3Context.test.tsx (trigger wiring).
 */
export function usePrimaryCustomerRateFetch({
  control,
  isEditingOrder,
  loadPrimaryCustomerRate,
  clearPrimaryCustomerRate,
}: {
  control: Control<OrderFormV3FieldValues>;
  isEditingOrder: boolean;
  loadPrimaryCustomerRate: (args: {
    accountId: string;
    companyId: string;
  }) => Promise<void>;
  clearPrimaryCustomerRate: () => void;
}): void {
  const account = useWatch({ control, name: 'account' });
  const company = useWatch({ control, name: 'company' });

  useEffect(() => {
    if (!isEditingOrder && account?.id && company?.id) {
      void loadPrimaryCustomerRate({ accountId: account.id, companyId: company.id });
    } else {
      clearPrimaryCustomerRate();
    }
  }, [
    account?.id,
    company?.id,
    isEditingOrder,
    loadPrimaryCustomerRate,
    clearPrimaryCustomerRate,
  ]);
}
